import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { type RefObject, useEffect, useRef, useState } from "react";
import { tweenLibrary } from "./tweenLibrary"; // Import tweenLibrary
import type { TweenOptions } from "./types";

// Define tween name type based on the click library keys
type ClickTweenName = keyof typeof tweenLibrary.click;

interface UseClickAnimationProps {
	figureRef: RefObject<HTMLElement>; // Element to attach the click listener
	imageRef: RefObject<HTMLImageElement>; // Specific element often needed for animation (e.g., image)
	tweenName: string | undefined; // Space-separated names of the click tweens to use
	options?: TweenOptions; // Optional parameters for the tweens
	navigation?: (e: MouseEvent) => void; // Optional navigation callback
}

export function useClickAnimation({
	figureRef,
	imageRef,
	tweenName,
	options,
	navigation,
}: UseClickAnimationProps) {
	//console.log("useClickAnimation");
	const [isFullScreen, setIsFullScreen] = useState(false); // State might be specific to certain tweens
	const clonedImageRef = useRef<HTMLImageElement | null>(null); // Ref might be specific
	const activeTimeline = useRef<gsap.core.Timeline | null>(null); // Ref to store the active master timeline
	const { contextSafe } = useGSAP({ scope: figureRef });

	// --- Cleanup Clone on Unmount ---
	useEffect(() => {
		// Cleanup function
		return () => {
			// Kill the main timeline if it exists
			if (activeTimeline.current) {
				activeTimeline.current.kill();
				activeTimeline.current = null;
			}
			// Remove the clone if it exists and kill its animations
			// The listener removal is handled internally by the tween now
			if (clonedImageRef.current) {
				gsap.killTweensOf(clonedImageRef.current); // Kill any ongoing animations
				clonedImageRef.current.remove(); // Remove from DOM
				clonedImageRef.current = null; // Clear ref
			}
			setIsFullScreen(false); // Reset state if applicable
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Empty dependency array for mount/unmount cleanup

	// --- Click Listener and Animation Logic ---
	useEffect(() => {
		const figureElement = figureRef.current;
		const imageElement = imageRef.current;

		// Parse tween names string into an array of valid tween keys
		const tweenNamesArray = typeof tweenName === 'string'
			? tweenName.split(' ').filter(name => name in tweenLibrary.click) as ClickTweenName[]
			: [];


		// Determine if any action (animation or navigation) is defined
		const hasAnimations = tweenNamesArray.length > 0;
		const hasNavigation = typeof navigation === 'function';
		const needsAction = hasAnimations || hasNavigation; // Does the hook need to do anything?

		// --- Main Click Handler ---
		const handleClick = contextSafe((e: MouseEvent) => {
			if (!figureElement) return; // Need the figure element regardless

			// --- Event Propagation Control ---
			if (needsAction) {
				// If animations or navigation are defined, prevent default and stop propagation
				e.preventDefault();
				e.stopPropagation();
			}

			// --- Prevent Re-triggering (only if animations are involved) ---
			// Check if already animating (e.g., fullscreen or timeline active)
			if (hasAnimations && (isFullScreen || activeTimeline.current?.isActive())) {
				return;
			}

			// --- Execute Actions ---
			const masterTimeline = gsap.timeline();
			let requiresImageElement = false;

			// Add animations to the master timeline
			if (hasAnimations) {
				for (const name of tweenNamesArray) {
					// Check if this specific tween requires the image element
					if (name === "dotThenFullscreen") { // Add other tweens if they also need it
						requiresImageElement = true;
					}

					const animationFn = tweenLibrary.click[name];
					if (animationFn) {
						// Validate required elements *before* calling the function
						if (requiresImageElement && !imageElement) {
							console.warn(`Image element ref not found, required for '${name}' tween. Skipping tween.`);
							continue; // Skip this specific tween
						}

						try {
							const tweenOrTimeline = animationFn({
								event: e,
								figureElement,
								imageElement: imageElement ? imageElement : undefined,
								clonedImageRef,
								setIsFullScreen,
								options,
							});

							if (tweenOrTimeline) {
								masterTimeline.add(tweenOrTimeline, ">"); // Add sequentially
							} else {
								console.warn(`Click tween "${name}" did not return a timeline or tween.`);
							}
						} catch (error) {
							console.error(`Error executing click tween "${name}":`, error);
						}
					} else {
						// This case should technically not happen due to the filter earlier, but good for safety
						console.warn(`Click tween "${name}" definition not found.`);
					}
				}
			}

			// Handle navigation
			if (hasNavigation) {
				if (hasAnimations) {
					// If there are animations, run navigation after they complete
					masterTimeline.call(() => navigation(e), [], ">"); // Ensure navigation runs after all animations
				} else {
					// If only navigation, run it immediately
					navigation(e);
				}
			}

			// Play the master timeline if it has animations
			if (hasAnimations && masterTimeline.duration() > 0) {
				activeTimeline.current = masterTimeline;
				masterTimeline.play();
			} else if (hasAnimations) {
				// Handle case where animations were added but resulted in zero duration
				//console.log("Master timeline created but has zero duration.");
				activeTimeline.current = null; // Clear active timeline if nothing played
			}
		});

		// Add the main click listener only if there's an action to perform
		if (needsAction) {
			figureElement?.addEventListener("click", handleClick);
		}

		// --- Cleanup for this effect instance ---
		// --- Cleanup for this effect instance ---
		return () => {
			if (needsAction) {
				figureElement?.removeEventListener("click", handleClick);
			}
			// Kill the timeline if the dependencies change or component unmounts
			if (activeTimeline.current) {
				activeTimeline.current.kill();
				activeTimeline.current = null;
			}
		};
	}, [
		figureRef,
		imageRef,
		tweenName, // Now a string
		options,
		navigation, // Added navigation callback
		contextSafe,
		isFullScreen, // Re-run if fullscreen state changes to prevent re-triggering
	]);

	// Hook doesn't return anything directly
}

